import React, { useMemo, useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  TextInput,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../types/navigation';
import { itemDisplayValue } from '../lib/itemValuation';
import { getUserProductById, updateUserProduct } from '../utils/userProductStorage';
import type { UserProductWithDetails, UpdateUserProduct, RemovalReason } from '../types/newDatabaseTypes';
import { useTheme } from '../styles/ThemeProvider';
import { useCommonStyles } from '../styles/useCommonStyles';
import { useDispatch } from 'react-redux';
import { fetchUserProducts } from '../store/slices/userProductsSlice';
import { useAuth } from '../context/AuthContext';

type Props = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'RemoveItem'>;
  route: RouteProp<RootStackParamList, 'RemoveItem'>;
};

export default function RemoveItemScreen({ navigation, route }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        scrollView: {
          flex: 1,
        },
        centered: {
          flex: 1,
          justifyContent: 'center',
          alignItems: 'center',
          padding: theme.spacing.xl,
        },
        loadingText: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
          marginTop: theme.spacing.md,
        },
        errorText: {
          ...theme.typography.md,
          color: theme.colors.destructive,
        },
        header: {
          paddingVertical: theme.spacing.lg,
          paddingHorizontal: theme.spacing.lg,
          borderBottomWidth: theme.borders.borderWidth,
          borderBottomColor: theme.borders.borderColor,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        headerTitle: {
          ...theme.typography.h3,
          color: theme.colors.textPrimary,
          marginBottom: theme.spacing.xs,
        },
        itemName: {
          ...theme.typography.md,
          color: theme.colors.textSecondary,
        },
        content: {
          padding: theme.spacing.lg,
        },
        fieldGroup: {
          marginTop: theme.spacing.lg,
        },
        reasonButtons: {
          flexDirection: 'row',
          flexWrap: 'wrap',
          gap: theme.spacing.sm,
          marginTop: theme.spacing.sm,
        },
        reasonButton: {
          paddingVertical: theme.spacing.sm,
          paddingHorizontal: theme.spacing.lg,
          backgroundColor: 'transparent',
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
        },
        reasonButtonActive: {
          backgroundColor: theme.colors.accent,
          borderColor: theme.colors.accent,
        },
        reasonButtonText: {
          ...theme.typography.sm,
          fontFamily: theme.fontFamily.bodyMedium,
          color: theme.colors.textPrimary,
        },
        reasonButtonTextActive: {
          color: theme.colors.backgroundPrimary,
        },
        textArea: {
          height: 100,
          textAlignVertical: 'top',
        },
        footer: {
          flexDirection: 'row',
          gap: theme.spacing.sm,
          padding: theme.spacing.lg,
          borderTopWidth: theme.borders.borderWidth,
          borderTopColor: theme.borders.borderColor,
          backgroundColor: theme.colors.backgroundPrimary,
        },
        footerButton: {
          flex: 1,
        },
      }),
    [theme],
  );
  const { itemId } = route.params;
  const { session } = useAuth();
  const dispatch = useDispatch<any>();
  const [item, setItem] = useState<UserProductWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [removalReason, setRemovalReason] = useState<RemovalReason>('sold');
  const [removalPrice, setRemovalPrice] = useState('');
  const [removalNotes, setRemovalNotes] = useState('');
  const [removing, setRemoving] = useState(false);

  useEffect(() => {
    fetchItem();
  }, [itemId]);

  const fetchItem = async () => {
    try {
      const product = await getUserProductById(itemId);
      if (product) {
        setItem(product);
        // Pre-fill with the item's displayed value only (V3 P6): never a pending or
        // unresolved item's legacy valuation.
        const displayed = itemDisplayValue(product);
        if (displayed) {
          setRemovalPrice(displayed.toString());
        }
      }
    } catch (err) {
      console.error('Error fetching item:', err);
      Alert.alert('Error', 'Failed to load item details');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    if (!item) return;

    try {
      setRemoving(true);

      const updates: UpdateUserProduct = {
        status: 'removed',
        removed_at: new Date().toISOString(),
        removal_reason: removalReason,
        removal_notes: removalNotes || null,
      };

      // Add removal price if provided
      if (removalPrice && !isNaN(parseFloat(removalPrice))) {
        updates.removal_price = parseFloat(removalPrice);
      }

      await updateUserProduct(item.id, updates);

      // Update Redux store to keep UI in sync
      if (session?.user?.id) {
        await dispatch(fetchUserProducts(session.user.id));
      }

      Alert.alert(
        'Item Removed',
        'The item has been removed from your cache.',
        [
          {
            text: 'OK',
            onPress: () => navigation.goBack(),
          },
        ]
      );
    } catch (err) {
      console.error('Error removing item:', err);
      Alert.alert('Error', 'Failed to remove item. Please try again.');
    } finally {
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={commonStyles.safeArea}>
        <View style={styles.centered}>
          <ActivityIndicator size={36} color={theme.colors.accent} />
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (!item) {
    return (
      <SafeAreaView style={commonStyles.safeArea}>
        <View style={styles.centered}>
          <Text style={styles.errorText}>Item not found</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={commonStyles.safeArea} edges={['top']}>
      <ScrollView style={styles.scrollView}>
        {/* Header */}
        <View style={styles.header}>
          <Text style={styles.headerTitle}>Remove Item</Text>
          <Text style={styles.itemName}>
            {item?.variant?.model?.family?.brand?.name && item?.variant?.model?.name
              ? `${item.variant.model.family.brand.name} ${item.variant.model.name}`
              : 'Item'}
          </Text>
        </View>

        <View style={styles.content}>
          <Text style={commonStyles.inputLabel}>Reason for removal</Text>
          <View style={styles.reasonButtons}>
            {(['sold', 'lost', 'given_away', 'other'] as const).map((reason) => (
              <TouchableOpacity
                key={reason}
                style={[
                  styles.reasonButton,
                  removalReason === reason && styles.reasonButtonActive,
                ]}
                onPress={() => setRemovalReason(reason)}
              >
                <Text
                  style={[
                    styles.reasonButtonText,
                    removalReason === reason && styles.reasonButtonTextActive,
                  ]}
                >
                  {reason === 'given_away' ? 'Given Away' : reason.charAt(0).toUpperCase() + reason.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>

          {removalReason === 'sold' && (
            <View style={styles.fieldGroup}>
              <Text style={commonStyles.inputLabel}>Sale price (optional)</Text>
              <TextInput
                style={commonStyles.input}
                value={removalPrice}
                onChangeText={setRemovalPrice}
                placeholder="Enter sale price"
                placeholderTextColor={theme.colors.textTertiary}
                keyboardType="decimal-pad"
              />
            </View>
          )}

          <View style={styles.fieldGroup}>
            <Text style={commonStyles.inputLabel}>Notes (optional)</Text>
            <TextInput
              style={[commonStyles.input, styles.textArea]}
              value={removalNotes}
              onChangeText={setRemovalNotes}
              placeholder="Add any notes about this removal"
              placeholderTextColor={theme.colors.textTertiary}
              multiline
              numberOfLines={4}
              textAlignVertical="top"
            />
          </View>
        </View>
      </ScrollView>

      <View style={styles.footer}>
        <TouchableOpacity
          style={[commonStyles.secondaryButton, styles.footerButton]}
          onPress={() => navigation.goBack()}
        >
          <Text style={commonStyles.secondaryButtonText}>Cancel</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={[commonStyles.destructiveButton, styles.footerButton, removing && commonStyles.disabledButton]}
          onPress={handleRemove}
          disabled={removing}
        >
          {removing ? (
            <ActivityIndicator color={theme.colors.textPrimary} />
          ) : (
            <Text style={commonStyles.destructiveButtonText}>Remove Item</Text>
          )}
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

